import { useCallback, useEffect, useState } from 'react'

/**
 * The name of the cookie used to store CTA popup suppression information.
 */
const COOKIE_NAME = 'cta_popup_suppression'

/**
 * The period of time (in milliseconds) for which a dismissed CTA popup should be suppressed.
 *
 * Currently set to 24 hours.
 */
const SUPRESSION_DURATION_MS = 24 * 60 * 60 * 1000

/**
 * Reads and parses the CTA popup suppression cookie, and returns a map of popup ID to timestamp.
 */
function readSuppressionCookie(): Record<string, number> {
  // Returns an empty object during SSR to avoid errors when accessing `document.cookie`.
  if (typeof document === 'undefined') return {}

  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${COOKIE_NAME}=`))

  if (!match) {
    return {}
  }

  try {
    return JSON.parse(decodeURIComponent(match.split('=')[1]))
  } catch {
    return {}
  }
}

/**
 * Writes the suppression map to the cookie.
 */
function writeSuppressionCookie(suppressions: Record<string, number>) {
  // The cookie expires in 24 hours for all popups unless the user dismisses a popup again, which will reset this.
  const expirationDate = new Date(Date.now() + SUPRESSION_DURATION_MS)
  const expirationDateUtc = expirationDate.toUTCString()

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(suppressions)
  )}; path=/; expires=${expirationDateUtc}; SameSite=Lax`
}

/**
 * Hook that manages CTA popup suppression state via a cookie.
 *
 * @param ctaPopupId The ID of the CTA popup to manage suppression for.
 * @returns An object containing the suppression state and a function to suppress the popup.
 */
export function useCtaPopupSuppression(ctaPopupId: string | undefined) {
  const [isSuppressed, setIsSuppressed] = useState(true)

  useEffect(() => {
    if (!ctaPopupId) {
      return
    }

    const suppressions = readSuppressionCookie()
    const suppressedAt = suppressions[ctaPopupId]

    // Mark as unsuppressed if the popup was never suppressed or it was suppressed more than 24 hours ago
    if (!suppressedAt || Date.now() - suppressedAt > SUPRESSION_DURATION_MS) {
      setIsSuppressed(false)
    }
  }, [ctaPopupId])

  const suppress = useCallback(() => {
    if (!ctaPopupId) {
      return
    }

    const suppressions = readSuppressionCookie()
    suppressions[ctaPopupId] = Date.now()
    writeSuppressionCookie(suppressions)
    setIsSuppressed(true)
  }, [ctaPopupId])

  return { isSuppressed, suppress }
}
